import * as THREE from 'three';
import Time from '../Utils/Time';
import Application from '../Application';
import { SCREEN_CONFIG, DESK_VIEW_CONFIG, IDLE_VIEW_CONFIG, ORBIT_VIEW_CONFIG, IDLE_ORBIT_SPEED, IDLE_ORBIT_PHASE_OFFSET, IDLE_ORBIT_X_AMPLITUDE_SCALE, IDLE_ORBIT_Y_AMPLITUDE, MOBILE_LANDSCAPE_DESK_VIEW_SCALE, MOBILE_PORTRAIT_DESK_VIEW_SCALE, MOBILE_PORTRAIT_DESK_POSITION_OFFSET, MOBILE_PORTRAIT_DESK_FOCAL_OFFSET, MOBILE_PORTRAIT_DESK_LOOK_AT_SCREEN, MOBILE_PORTRAIT_VIEW_SCALE, MOBILE_PORTRAIT_IDLE_X_OFFSET, MOBILE_PORTRAIT_IDLE_POSITION, MOBILE_PORTRAIT_IDLE_ORBIT_AMPLITUDE_X, MOBILE_PORTRAIT_IDLE_ORBIT_AMPLITUDE_Y, } from '../World/deskSceneConfig';
function getScreenPos() {
    const [x, y, z] = SCREEN_CONFIG.position;
    return new THREE.Vector3(x, y, z);
}
function getScreenEuler() {
    const [rx, ry, rz] = SCREEN_CONFIG.rotation;
    return new THREE.Euler(rx * (Math.PI / 180), ry * (Math.PI / 180), rz * (Math.PI / 180), 'XYZ');
}
function getScreenNormal() {
    return new THREE.Vector3(0, 0, 1).applyEuler(getScreenEuler());
}
function getScreenRight() {
    return new THREE.Vector3(1, 0, 0).applyEuler(getScreenEuler());
}
function getScreenUp() {
    return new THREE.Vector3(0, 1, 0).applyEuler(getScreenEuler());
}
function getMonitorKeyframe() {
    var _a, _b;
    const baseDist = (_a = SCREEN_CONFIG.monitorViewDistance) !== null && _a !== void 0 ? _a : 2000;
    const screenPos = getScreenPos();
    const pan = (_b = SCREEN_CONFIG.monitorPanOffset) !== null && _b !== void 0 ? _b : [0, 0];
    const focal = screenPos
        .clone()
        .add(getScreenRight().multiplyScalar(pan[0]))
        .add(getScreenUp().multiplyScalar(pan[1]));
    const normal = getScreenNormal();
    const cameraPos = focal.clone().add(normal.clone().multiplyScalar(baseDist));
    return {
        position: cameraPos,
        focalPoint: focal,
    };
}
export class CameraKeyframeInstance {
    constructor(keyframe) {
        this.position = keyframe.position;
        this.focalPoint = keyframe.focalPoint;
    }
    update() { }
}
const keys = {
    idle: {
        position: new THREE.Vector3(IDLE_VIEW_CONFIG.position[0], IDLE_VIEW_CONFIG.position[1], IDLE_VIEW_CONFIG.position[2]),
        focalPoint: new THREE.Vector3(IDLE_VIEW_CONFIG.focalPoint[0], IDLE_VIEW_CONFIG.focalPoint[1], IDLE_VIEW_CONFIG.focalPoint[2]),
    },
    monitor: getMonitorKeyframe(),
    desk: {
        position: new THREE.Vector3(DESK_VIEW_CONFIG.position[0], DESK_VIEW_CONFIG.position[1], DESK_VIEW_CONFIG.position[2]),
        focalPoint: new THREE.Vector3(DESK_VIEW_CONFIG.focalPoint[0], DESK_VIEW_CONFIG.focalPoint[1], DESK_VIEW_CONFIG.focalPoint[2]),
    },
    loading: {
        position: new THREE.Vector3(-35000, 35000, 35000),
        focalPoint: new THREE.Vector3(0, -5000, 0),
    },
    orbitControlsStart: {
        position: new THREE.Vector3(ORBIT_VIEW_CONFIG.position[0], ORBIT_VIEW_CONFIG.position[1], ORBIT_VIEW_CONFIG.position[2]),
        focalPoint: new THREE.Vector3(ORBIT_VIEW_CONFIG.focalPoint[0], ORBIT_VIEW_CONFIG.focalPoint[1], ORBIT_VIEW_CONFIG.focalPoint[2]),
    },
};
export class MonitorKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.monitor;
        super(keyframe);
        this.application = new Application();
        this.sizes = this.application.sizes;
    }
    update() {
        var _a, _b, _c, _d, _e, _f, _g;
        const isMobile = this.sizes.isMobile();
        let baseDist = isMobile
            ? ((_b = (_a = SCREEN_CONFIG.monitorViewDistanceMobile) !== null && _a !== void 0 ? _a : SCREEN_CONFIG.monitorViewDistance) !== null && _b !== void 0 ? _b : 2800)
            : ((_c = SCREEN_CONFIG.monitorViewDistance) !== null && _c !== void 0 ? _c : 2000);
        // On mobile portrait: use config so you can tune in deskSceneConfig.ts (higher = more zoomed out)
        if (this.sizes.isMobilePortrait()) {
            baseDist *= (_d = SCREEN_CONFIG.monitorViewMobilePortraitZoom) !== null && _d !== void 0 ? _d : 0.85;
        }
        // On mobile landscape: use config so you can tune in one place (deskSceneConfig.ts)
        if (this.sizes.isMobileLandscape()) {
            baseDist *= (_e = SCREEN_CONFIG.monitorViewMobileLandscapeZoom) !== null && _e !== void 0 ? _e : 0.78;
        }
        const aspectScale = (_f = SCREEN_CONFIG.monitorZoomAspectScale) !== null && _f !== void 0 ? _f : 1200;
        const aspect = this.sizes.width > 0 ? this.sizes.height / this.sizes.width : 9 / 16;
        const additionalZoom = isMobile ? 0 : 600;
        let distance = baseDist + aspect * aspectScale - additionalZoom;
        if (!Number.isFinite(distance) || distance <= 0)
            distance = baseDist;
        const pan = (_g = SCREEN_CONFIG.monitorPanOffset) !== null && _g !== void 0 ? _g : [0, 0];
        const screenPos = getScreenPos();
        this.focalPoint
            .copy(screenPos)
            .add(getScreenRight().clone().multiplyScalar(pan[0]))
            .add(getScreenUp().clone().multiplyScalar(pan[1]));
        const normal = getScreenNormal();
        this.position.copy(this.focalPoint).add(normal.multiplyScalar(distance));
        // Mobile portrait: raise camera and look down at screen (like desktop) so it doesn't look tilted
        if (this.sizes.isMobilePortrait()) {
            this.position.y += 400;
        }
    }
}
export class LoadingKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.loading;
        super(keyframe);
    }
    update() { }
}
export class DeskKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.desk;
        super(keyframe);
        this.application = new Application();
        this.mouse = this.application.mouse;
        this.sizes = this.application.sizes;
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.targetFoc = new THREE.Vector3().copy(keyframe.focalPoint);
        this.targetPos = new THREE.Vector3().copy(keyframe.position);
    }
    update() {
        this.targetFoc.x +=
            (this.mouse.x - this.sizes.width / 2 - this.targetFoc.x) * 0.05;
        this.targetFoc.y +=
            (-(this.mouse.y - this.sizes.height) - this.targetFoc.y) * 0.05;
        this.targetPos.x +=
            (this.mouse.x - this.sizes.width / 2 - this.targetPos.x) * 0.025;
        this.targetPos.y +=
            (-(this.mouse.y - this.sizes.height * 2) - this.targetPos.y) *
                0.025;
        // On mobile use a fixed aspect so the viewing angle stays "front" (not rotated ~90° to the side)
        const aspect = this.sizes.isMobile()
            ? 9 / 16
            : (this.sizes.width > 0 ? this.sizes.height / this.sizes.width : 9 / 16);
        this.targetPos.z = this.origin.z + aspect * 3000 - 1800;
        if (!Number.isFinite(this.targetPos.z))
            this.targetPos.z = this.origin.z;
        this.focalPoint.copy(this.targetFoc);
        this.position.copy(this.targetPos);
        if (this.sizes.isMobile()) {
            const scale = this.sizes.isMobileLandscape() ? MOBILE_LANDSCAPE_DESK_VIEW_SCALE : MOBILE_PORTRAIT_DESK_VIEW_SCALE;
            this.position.sub(this.focalPoint).multiplyScalar(scale).add(this.focalPoint);
            if (this.sizes.isMobilePortrait()) {
                this.position.x += MOBILE_PORTRAIT_DESK_POSITION_OFFSET[0];
                this.position.y += MOBILE_PORTRAIT_DESK_POSITION_OFFSET[1];
                this.position.z += MOBILE_PORTRAIT_DESK_POSITION_OFFSET[2];
                if (MOBILE_PORTRAIT_DESK_LOOK_AT_SCREEN) {
                    this.focalPoint.copy(getScreenPos());
                }
                else {
                    this.focalPoint.x += MOBILE_PORTRAIT_DESK_FOCAL_OFFSET[0];
                    this.focalPoint.y += MOBILE_PORTRAIT_DESK_FOCAL_OFFSET[1];
                    this.focalPoint.z += MOBILE_PORTRAIT_DESK_FOCAL_OFFSET[2];
                }
            }
        }
    }
}
export class IdleKeyframe extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.idle;
        super(keyframe);
        this.origin = new THREE.Vector3().copy(keyframe.position);
        this.time = new Time();
        this.application = new Application();
        this.sizes = this.application.sizes;
    }
    update() {
        this.position.x =
            Math.sin((this.time.elapsed + 19000 + IDLE_ORBIT_PHASE_OFFSET) * IDLE_ORBIT_SPEED) *
                IDLE_ORBIT_X_AMPLITUDE_SCALE *
                this.origin.x;
        this.position.y =
            Math.sin((this.time.elapsed + 1000 + IDLE_ORBIT_PHASE_OFFSET) * 0.000004) * IDLE_ORBIT_Y_AMPLITUDE +
                this.origin.y -
                3000;
        this.position.z = this.position.z;
        if (this.sizes.isMobile()) {
            if (this.sizes.isMobilePortrait() && MOBILE_PORTRAIT_IDLE_POSITION) {
                const [bx, by, bz] = MOBILE_PORTRAIT_IDLE_POSITION;
                const orbitX = Math.sin((this.time.elapsed + 19000 + IDLE_ORBIT_PHASE_OFFSET) * IDLE_ORBIT_SPEED) * MOBILE_PORTRAIT_IDLE_ORBIT_AMPLITUDE_X;
                const orbitY = Math.sin((this.time.elapsed + 1000 + IDLE_ORBIT_PHASE_OFFSET) * 0.000004) * MOBILE_PORTRAIT_IDLE_ORBIT_AMPLITUDE_Y;
                this.position.set(bx + orbitX, by + orbitY, bz);
            }
            else {
                if (this.sizes.isMobilePortrait())
                    this.position.x += MOBILE_PORTRAIT_IDLE_X_OFFSET;
                const scale = this.sizes.isMobileLandscape() ? MOBILE_LANDSCAPE_DESK_VIEW_SCALE : MOBILE_PORTRAIT_VIEW_SCALE;
                const safeScale = Number.isFinite(scale) ? Math.max(0.2, Math.min(5, scale)) : 1;
                this.position.sub(this.focalPoint).multiplyScalar(safeScale).add(this.focalPoint);
            }
        }
    }
}
export class OrbitControlsStart extends CameraKeyframeInstance {
    constructor() {
        const keyframe = keys.orbitControlsStart;
        super(keyframe);
    }
    update() { }
}
